import React from 'react';
import { useBrandClassificationSplineMetrics } from './hooks/useBrandClassificationMetrics';
import { brandClassificationResponseToSpline } from '../utils/trafficParsers';
import { getAdjustedDataSetsForSpline, getSplineTotals, saveSplineAsDelimitedData } from 'src/utils/chartUtils';
import { calculatePercentChange } from 'src/utils/app';
import BeaconChartWithLegend from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconChartWithLegend';
import {
  useAppSelector,
  useComparisonTimePeriodShortDisplayName,
  useMainTimePeriodShortDisplayName,
  useMetricFormatter
} from 'src/utils/Hooks';
import { METRICTYPE } from 'src/utils/entityDefinitions';
import SplineChart from 'src/components/BeaconV2Components/SplineChart/SplineChart';
import { getDirection } from 'src/components/BeaconV2Components/utils/chartStyles';
import { useCompanyName } from 'src/utils/Hooks/reduxSelectors';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';
import {
  useEntityPhrasing,
  useTaxonomySubtitleText
} from 'src/components/BeaconV2Components/common/BeaconProDynamicSubtitle/hooks';
import { BeaconSubtitleContainer } from 'src/components/BeaconV2Components/BeaconChartWithLegend/BeaconSubtitleContainer';
import SubtitleFilters from 'src/components/Charts/SubtitleFilters';

/**
 * Shows keyword count with traffic for topics
 */
export const KeywordCountForTopics = ({
  keywordType
}: {
  keywordType: 'totalTrafficKeywordCount' | 'organicTrafficKeywordCount';
}) => {
  const companyName = useCompanyName();
  const mainTimePeriodName = useMainTimePeriodShortDisplayName();
  const comparisonTimePeriodName = useComparisonTimePeriodShortDisplayName();

  const { displayName: mainPeriodDisplay } = useAppSelector((state) => state.mainTimePeriod);
  const { displayName: comparisonPeriodDisplay } = useAppSelector((state) => state.comparisonTimePeriod);

  const formatMetric = useMetricFormatter();
  const { response, isLoading } = useBrandClassificationSplineMetrics();

  const rawPrimaryData = brandClassificationResponseToSpline(response[0], keywordType);
  const rawSecondaryData = brandClassificationResponseToSpline(response[1], keywordType);

  const { primaryData, secondaryData } = getAdjustedDataSetsForSpline(rawPrimaryData, rawSecondaryData);
  const { primaryTotal, secondaryTotal } = getSplineTotals(rawPrimaryData, rawSecondaryData, { func: 'last' });
  const percentChange = calculatePercentChange(primaryTotal, secondaryTotal);

  const chartTitle = {
    totalTrafficKeywordCount: 'Total Traffic',
    organicTrafficKeywordCount: 'Organic Traffic'
  }[keywordType];

  const entityPhrase = useEntityPhrasing();
  const taxonomyText = useTaxonomySubtitleText();

  const getSubtitle = () => {
    return (
      <BeaconSubtitleContainer>
        <Text variant="body1">
          {`The chart displays the unique number of keywords that generated ${chartTitle.toLowerCase()} for ${companyName}`}
          {taxonomyText}
          {` in ${entityPhrase}`}
          {` during the ${mainTimePeriodName} period compared to the ${comparisonTimePeriodName}.`}
          <SubtitleFilters />
        </Text>
      </BeaconSubtitleContainer>
    );
  };

  return (
    <BeaconChartWithLegend
      title={`Keyword Count with ${chartTitle}`}
      loading={isLoading}
      includeSummaryInfo={false}
      primaryLegendProps={{
        metric: formatMetric(primaryTotal, METRICTYPE.VOLUME, { decimalPlaces: 2, showFullValue: false }),
        displayName: mainPeriodDisplay,
        change: formatMetric(Math.abs(percentChange), METRICTYPE.PERCENT, { decimalPlaces: 2 }),
        direction: getDirection(percentChange)
      }}
      comparisonLegendProps={{
        metric: formatMetric(secondaryTotal, METRICTYPE.VOLUME, { decimalPlaces: 2, showFullValue: false }),
        displayName: comparisonPeriodDisplay
      }}
      convertSeriesToDelimitedData={async () =>
        saveSplineAsDelimitedData(primaryData, secondaryData, `Keyword Count with ${chartTitle}`)
      }
      subtitle={getSubtitle()}
    >
      <SplineChart metricType={METRICTYPE.VOLUME} primaryData={primaryData} secondaryData={secondaryData} />
    </BeaconChartWithLegend>
  );
};
